<template>
    <div class="checkout-confirmation-page">
        <span v-if="booking && ['failed', 'aborted'].indexOf(booking.status) === -1">
            <span class="emoji">🎉</span>

            <!-- Heading -->
            <h1>
                {{ 'checkout.thankYou.heading' | trans }}
            </h1>
        </span>

        <!-- Status bar -->
        <div class="status-bar">
            <!-- Booking number -->
            <span class="booking-number">
                {{ 'checkout.thankYou.bookingNumber' | trans({ number: bookingId }) }}
            </span>

            &bull;

            <!-- Status: Open -->
            <span class="status status-in-progress" v-if="isLoading || (booking && booking.status === 'open')">
                {{ 'checkout.thankYou.status.in_progress' | trans }}
                <i class="far fa-spinner-third fa-spin ml-1"></i>
            </span>

            <!-- Status: Completed -->
            <span class="status status-completed" v-if="! isLoading && booking && booking.status === 'completed'">
                {{ 'checkout.thankYou.status.completed' | trans }}
                <i class="far fa-check ml-1"></i>
            </span>

            <!-- Status: Test -->
            <span class="status status-test" v-if="! isLoading && booking && booking.status === 'test'">
                <i class="far fa-vial mr-1"></i>
                {{ 'checkout.thankYou.status.test' | trans }}
            </span>

            <!-- Status: Failed -->
            <span class="status status-failed" v-if="! isLoading && booking && booking.status === 'failed'">
                <i class="far fa-circle-exclamation mr-1"></i>
                {{ 'checkout.thankYou.status.failed' | trans }}
            </span>

            <!-- Status: Aborted -->
            <span class="status status-aborted" v-if="! isLoading && booking && booking.status === 'aborted'">
                <i class="far fa-times mr-1"></i>
                {{ 'checkout.thankYou.status.aborted' | trans }}
            </span>

            <!-- Status: Cancelled -->
            <span class="status status-cancelled" v-if="! isLoading && booking && booking.status === 'cancelled'">
                {{ 'checkout.thankYou.status.cancelled' | trans }}
            </span>
        </div>

        <!-- Progress card -->
        <div class="card" v-if="data && data.status === 'in_progress'">
            <div class="card-body">
                <span class="card-text float-right ml-2">
                    {{ 'checkout.thankYou.progressCard.steps' | trans({ current: data.steps.current, total: data.steps.total }) }}
                </span>

                <h3 class="card-title">
                    {{ 'checkout.thankYou.progressCard.heading' | trans }}
                </h3>
                
                <!-- Current action description -->
                <p class="card-text" v-if="data && data.current_action">
                    {{ 'checkout.thankYou.bookingPipelineActionDescriptions.' + data.current_action | trans }}
                </p>

                <p class="card-text" v-if="! data || (data && ! data.current_action)">
                    {{ 'checkout.thankYou.bookingPipelineActionDescriptions.default' | trans }}
                </p>

                <!-- Progress bar -->
                <div class="progress mt-3">
                    <div class="progress-bar" role="progressbar" :style="{ width: progress + '%' }"></div>
                </div>
            </div>
        </div>

        <!-- Failed + waiting pipeline -->
        <div class="card" v-if="data && ['failed', 'waiting'].indexOf(data.status) !== -1">
            <div class="card-body">
                <h3 class="card-title">
                    {{ 'checkout.thankYou.failedCard.heading' | trans }}
                </h3>

                <p class="card-text">
                    {{ 'checkout.thankYou.failedCard.info' | trans }}
                </p>

                <!-- Only show button for failed pipelines -->
                <a :href="$window.route('frontend.support.contactForm.show')" class="btn btn-primary btn-lg btn-block" target="_blank" v-if="data.status === 'failed'">
                    {{ 'checkout.thankYou.failedCard.helpButton' | trans }}
                </a>
            </div>
        </div>

        <!-- Cancelled payments -->
        <div class="card" v-if="booking && booking.status === 'aborted' && booking.has_cancelled_payments === true">
            <div class="card-body">
                <h3 class="card-title">
                    {{ 'checkout.thankYou.cancelledPaymentCard.heading' | trans }}
                </h3>

                <p class="card-text">
                    {{ 'checkout.thankYou.cancelledPaymentCard.info' | trans }}
                </p>

                <!-- Change payment type -->
                <a :href="payUrl" class="btn btn-primary btn-lg btn-block">
                    {{ 'checkout.thankYou.cancelledPaymentCard.tryAgainButton' | trans }}
                    <i class="wa-icon-long-arrow-right ml-2 float-right" style="margin-top: 3px;"></i>
                </a>

                <!-- Abort booking -->
                <a :href="$window.route('frontend.checkout.cancel', { booking: bookingToken })" class="btn btn-danger btn-lg btn-block">
                    {{ 'checkout.thankYou.cancelledPaymentCard.abortButton' | trans }}
                    <i class="wa-icon-long-arrow-right ml-2 float-right" style="margin-top: 3px;"></i>
                </a>
            </div>
        </div>

        <!-- Payment required -->
        <div class="card" v-if="booking && booking.status === 'completed' && booking.is_payment_required === true">
            <div class="card-body">
                <h3 class="card-title">
                    {{ 'checkout.thankYou.paymentRequiredCard.heading' | trans }}
                </h3>

                <p class="card-text">
                    {{ 'checkout.thankYou.paymentRequiredCard.info' | trans({ total: booking.open_total }) }}
                </p>

                <!-- Pay -->
                <a :href="payUrl" class="btn btn-primary btn-lg btn-block">
                    {{ 'checkout.thankYou.paymentRequiredCard.pay' | trans }}
                    <i class="wa-icon-long-arrow-right ml-2 float-right" style="margin-top: 3px;"></i>
                </a>
            </div>
        </div>

        <!-- Completed -->
        <div class="mt-5" v-if="booking && ['completed', 'test'].indexOf(booking.status) !== -1 && (! data || (data && data.status === 'completed'))">
            <!-- Booking information -->
            <h2>
                {{ 'checkout.thankYou.bookingInformation' | trans }}
            </h2>

            <p>
                {{ 'checkout.thankYou.bookingInformationReady' | trans }}
            </p>

            <a class="btn btn-primary btn-lg btn-block" :href="$window.route('pdf.bookings.information', { booking: bookingToken })" target="_blank">
                <i class="wa-icon-pdf-file"></i>
                {{ 'checkout.thankYou.openBookingInformation' | trans }}
            </a>

            <div class="divider"></div>

            <!-- Review: Checkout -->
            <checkout-review :booking-token="bookingToken"></checkout-review>
            
            <div class="divider"></div>

            <!-- Summary -->
            <div class="card">
                <div class="card-body">
                    <h3 class="card-title">
                        {{ 'checkout.thankYou.summary.heading' | trans }}
                    </h3>

                    <!-- Booking number -->
                    <div class="row">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.bookingNumber' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ bookingId }}
                            </span>
                        </div>
                    </div>

                    <!-- Stay -->
                    <div class="row" v-if="booking.travel_period && booking.packages.length > 0">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.arrival' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.nights' | choice($moment(booking.travel_period.departure_date).diff($moment(booking.travel_period.arrive_date), 'days'), { nights: $moment(booking.travel_period.departure_date).diff($moment(booking.travel_period.arrive_date), 'days') }) }},
                                {{ 'checkout.thankYou.summary.rooms' | trans({ count: booking.packages.length }) }}
                            </span>
                        </div>
                    </div>

                    <!-- Arrival -->
                    <div class="row" v-if="booking.travel_period">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.arrival' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ $moment(booking.travel_period.arrive_date).format('dddd, DD. MMMM YYYY') }}
                            </span>
                        </div>
                    </div>

                    <!-- Departure -->
                    <div class="row" v-if="booking.travel_period">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.arrival' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ $moment(booking.travel_period.departure_date).format('dddd, DD. MMMM YYYY') }}
                            </span>
                        </div>
                    </div>

                    <!-- Payment method -->
                    <div class="row">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.paymentMethod' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ 'checkout.thankYou.paymentMethods.' + booking.payment_type | trans }}
                            </span>
                        </div>
                    </div>

                    <!-- Vouchers -->
                    <div class="row" v-if="booking.product_purchases.length > 0">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.vouchers' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text" v-for="purchase in booking.product_purchases" :key="'purchase_' + purchase.id">
                                1x {{ purchase.product.name }}

                                <small class="d-block" v-html="purchase.product.subtitle"></small>
                            </span>
                        </div>
                    </div>

                    <!-- Total -->
                    <div class="row">
                        <div class="col-md-5">
                            <span class="card-text">
                                {{ 'checkout.thankYou.summary.total' | trans }}
                            </span>
                        </div>

                        <div class="col-md-7">
                            <span class="card-text">
                                {{ booking.price.total | formatPrice }}
                                &euro;
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="divider"></div>

            <!-- Booking invoice -->
            <h2>
                {{ 'checkout.thankYou.invoice' | trans }}
            </h2>

            <p>
                {{ 'checkout.thankYou.invoiceInfo' | trans }}
            </p>

            <a class="btn btn-primary btn-lg btn-block" :href="$window.route('pdf.bookings.invoices.preview', { booking: bookingToken })" target="_blank">
                <i class="wa-icon-money-exchange"></i>
                {{ 'checkout.thankYou.openInvoice' | trans }}
            </a>
        </div>
    </div>
</template>

<script>
    import Echo from 'laravel-echo';

    export default {
        props: ['bookingId', 'bookingToken', 'payUrl'],
        data() {
            return {
                Echo: null,

                progress: 0,
                data: null,
                isLoading: true,
                booking: null,
            }
        },
        mounted() {
            this.Echo = new Echo({
                broadcaster: 'pusher',
                key: 'ff63da2a97603dcefac6',
                cluster: 'eu',
                encrypted: true,
                disableStats: true,
            });

            var self = this;
            this.Echo.channel('bookings.' + this.bookingId)
            .listen('.bookings.pipeline_update', (event) => {
                self.handleEvent(event);
            })
            .listen('.bookings.failed', (event) => {
                self.handleBookingFailed(event);
            })
            .listen('.bookings.aborted', (event) => {
                self.handleBookingFailed(event);
            })
            .listen('.bookings.paid', (event) => {
                self.loadBooking()
            })

            this.loadBooking()
        },
        computed: {},
        methods: {
            handleEvent(event) {
                console.log('Event', event);

                // Update progress
                this.progress = event.progress

                // Update data
                this.data = event

                // Check to reload booking
                if (['completed', 'failed', 'waiting', 'aborted'].indexOf(event.status) !== -1) {
                    this.loadBooking()
                }
            },
            
            handleBookingFailed(event) {
                this.booking = event

                // Reload to really make sure it failed
                this.loadBooking()
            },

            loadBooking() {
                var self = this;

                axios.get(route('api.bookings.show', {
                    booking: this.bookingToken,
                })).then((response) => {
                    self.booking = response.data.booking
                    self.data = response.data.pipeline
                    self.isLoading = false
                }).catch((error) => {
                    console.log(error);
                });
            },
        },
        watch: {}
    }
</script>
